import { useMutation } from '@apollo/client';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Typography from '@mui/material/Typography';
import DownloadIcon from '@mui/icons-material/Download';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { REQUEST_DATA_EXPORT } from '../../../helpers/graphql/dataExport';

// Self-contained on purpose: the rest of Settings.js is a Query/Mutation
// render-prop tree wired to changeSettings, and this card must never touch
// that machinery. The mutation mints a one-time token; the browser then
// navigates to /export, which streams the ZIP (billing-portal pattern).
const SECTIONS = [
  {
    key: 'posts',
    label: 'Posts',
    hint: 'Every published post as HTML plus its metadata',
    default: true,
  },
  {
    key: 'commentsReceived',
    label: 'Replies to your posts',
    hint: 'All comment threads under your posts',
    default: true,
  },
  {
    key: 'commentsWritten',
    label: 'Comments you wrote on other posts',
    default: false,
  },
  {
    key: 'drafts',
    label: 'Drafts',
    default: true,
  },
  {
    key: 'media',
    label: 'Photos & media',
    hint: 'Your original uploads, full resolution — this can be a large download',
    default: false,
  },
];

export default function DataExport() {
  const [selected, setSelected] = useState(() => {
    const initial = {};
    SECTIONS.forEach(section => {
      initial[section.key] = section.default;
    });
    return initial;
  });
  const [downloading, setDownloading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [requestExport, { loading }] = useMutation(REQUEST_DATA_EXPORT);

  const handleDownload = () => {
    const include = SECTIONS.map(s => s.key).filter(key => selected[key]);
    requestExport({ variables: { include } })
      .then(({ data }) => {
        const result = data && data.requestDataExport;
        if (result && result.success && result.token) {
          setDownloading(true);
          // Same-tab navigation on every platform: the response is an
          // attachment, so the page never unloads — iOS Safari hands it to
          // the download manager. (No window.open here: after the await it
          // can be popup-blocked on iOS, silently wasting the hourly token.)
          window.location.href = `/export?t=${encodeURIComponent(
            result.token,
          )}`;
          // The navigation stays on this page (attachment download), so
          // re-enable the button after the download has had time to start.
          setTimeout(() => setDownloading(false), 10000);
        } else {
          enqueueSnackbar(
            (result && result.message) || 'Could not create the export.',
            { variant: 'error' },
          );
        }
      })
      .catch(err => {
        console.error(err);
        enqueueSnackbar('Could not create the export.', { variant: 'error' });
      });
  };

  return (
    <>
      <Typography variant="h6" component="h2" className="pt-4" gutterBottom>
        Your data
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Download an archive of your content: your posts, comments, drafts and
        original photos. Your profile and account details are always included.
      </Typography>
      <FormGroup>
        {SECTIONS.map(section => (
          <div key={section.key}>
            <FormControlLabel
              labelPlacement="end"
              control={
                <Checkbox
                  checked={selected[section.key]}
                  onChange={event =>
                    setSelected({
                      ...selected,
                      [section.key]: event.target.checked,
                    })
                  }
                  value={section.key}
                  color="primary"
                />
              }
              label={section.label}
            />
            {section.hint && (
              <span className="tw-mb-1 tw-ml-8 tw--mt-2 tw-block tw-text-xs tw-text-gray-500">
                {section.hint}
              </span>
            )}
          </div>
        ))}
      </FormGroup>
      <Button
        variant="contained"
        color="primary"
        className="tw-mt-2"
        disabled={loading || downloading}
        onClick={handleDownload}
        startIcon={<DownloadIcon />}
      >
        {loading || downloading ? 'Preparing your archive…' : 'Download archive'}
      </Button>
      <span className="tw-mt-2 tw-block tw-text-xs tw-text-gray-500">
        You can create three exports per hour. Large photo libraries can take
        a few minutes to download.
      </span>
      <Divider className="tw-pt-6" />
    </>
  );
}
